import {
  faBuilding,
  faQuestionCircle,
  faSave,
  faUser
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Flex from 'components/common/Flex';
import SearchInputWithSuggestions from 'components/common/SearchInputWithSuggestions';
import TagSearch from 'components/tags/TagSearch';
import useApi from 'helpers/api';
import useNotification from 'hooks/useNotification';
import { useEffect, useRef, useState } from 'react';
import {
  Badge,
  Button,
  Form,
  Modal,
  OverlayTrigger,
  Tooltip
} from 'react-bootstrap';
import MaskedInput from 'react-text-mask';
import ContactsService from 'services/Contacts.service';

const ContactRegistrationModal = ({
  showModal,
  handleClose,
  isSuccess,
  customer,
  newContact
}) => {
  const api = useApi();
  const { showSuccessNotification, showErrorNotification } = useNotification();
  const [selectedCustomer, setSelectedCustomer] = useState({});
  const [customers, setCustomers] = useState([]);
  const [showMenuMainCustomer, setShowMenuMainCustomer] = useState(false);
  const [filters, setFilters] = useState({
    size: 100,
    includeInactives: false,
    name: ''
  });
  const initialState = {
    name: '',
    email: '',
    phone: '',
    landline: '',
    inactive: false,
    isPortalUser: false,
    isAdmin: false,
    customer: { id: '' },
    tags: []
  };
  const [body, setBody] = useState(initialState);
  const [validated, setValidated] = useState(false);
  const form = useRef(null);

  useEffect(() => {
    getCustomers();
  }, [filters.name]);

  useEffect(() => {
    setSelectedCustomer(customer);
    customer &&
      setFilters(prevFilters => ({ ...prevFilters, name: customer.name }));
  }, [customer]);

  useEffect(() => {
    if (showModal) {
      clearFields();
      setValidated(false);
      setShowMenuMainCustomer(false);
    }
  }, [showModal]);

  const getCustomers = async () => {
    if (filters.name != '') {
      const queryParams = new URLSearchParams(filters).toString();
      let response = await api.customers.list(queryParams);
      setCustomers(response.content);
    }
  };

  const onSubmit = async e => {
    if (form.current.checkValidity() === false) {
      e.preventDefault();
      e.stopPropagation();
      setValidated(true);
    } else {
      setValidated(false);

      const newBody = {
        name: body.name,
        email: body.email === '' ? null : body.email,
        phone: body.phone === '' ? null : body.phone,
        landline: body.landline === '' ? null : body.landline,
        inactive: body.inactive,
        isPortalUser: body.isPortalUser,
        isAdmin: body.isAdmin,
        customer: {
          id: selectedCustomer ? selectedCustomer.id : body.customer.id
        },
        tags: body.tags
      };

      const response = await ContactsService.create(newBody);
      if (response.status) {
        showErrorNotification(response.userMessage);
        isSuccess(false);
        setValidated(true);
      } else {
        isSuccess(true);
        newContact(response);
        showSuccessNotification('Contato cadastrado com sucesso');
        handleClose();
        clearFields();
      }
    }
  };

  const handleCustomerSelect = customer => {
    setFilters({ ...filters, name: customer.name });
    setBody({ ...body, customer: { id: customer.id } });
    setShowMenuMainCustomer(false);
  };

  const clearFields = () => {
    setBody(initialState);
    setFilters({ ...filters, name: '' });
  };

  const handleAddTag = tag => {
    if (tag.trim() !== '' && !body.tags.includes(tag.trim())) {
      setBody(prevState => ({
        ...prevState,
        tags: [...prevState.tags, tag.trim()]
      }));
    }
  };

  const handleRemoveTag = tag => {
    setBody(prevState => ({
      ...prevState,
      tags: prevState.tags.filter(t => t !== tag)
    }));
  };

  return (
    <>
      <Modal show={showModal} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>Novo contato</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form validated={validated} ref={form}>
            <Form.Group className="mb-3">
              <Form.Label>
                Nome <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                required
                type="text"
                placeholder="Nome"
                autoFocus
                value={body.name}
                onChange={e =>
                  setBody(prevState => ({ ...prevState, name: e.target.value }))
                }
              />

              <Form.Control.Feedback type="invalid">
                Insira um nome.
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3" controlId="exampleForm.ControlInput1">
              <Form.Label>
                E-mail{' '}
                {(body.isPortalUser ||
                  (!body.phone?.trim() && !body.landline?.trim())) && (
                  <span className="text-danger">*</span>
                )}
              </Form.Label>
              <Form.Control
                required={
                  (!body.phone?.trim() && !body.landline?.trim()) ||
                  body.isPortalUser
                }
                type="email"
                placeholder="E-mail"
                value={body.email}
                onChange={e =>
                  setBody(prevState => ({
                    ...prevState,
                    email: e.target.value
                  }))
                }
              />

              <Form.Control.Feedback type="invalid">
                Insira um e-mail.
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3" controlId="agent.phone">
              <Form.Label>
                Telefone{' '}
                {body.email === '' && body.phone === '' && (
                  <span className="text-danger">*</span>
                )}
              </Form.Label>
              <MaskedInput
                mask={[
                  '(',
                  /[1-9]/,
                  /\d/,
                  ')',
                  ' ',
                  /\d/,
                  /\d/,
                  /\d/,
                  /\d/,
                  '-',
                  /\d/,
                  /\d/,
                  /\d/,
                  /\d/
                ]}
                placeholder="(00) 0000-0000"
                required={body.email === '' && body.phone === ''}
                guide={false}
                value={body.landline}
                onChange={e =>
                  setBody(prevBody => ({
                    ...prevBody,
                    landline: e.target.value
                  }))
                }
                render={(ref, props) => <Form.Control ref={ref} {...props} />}
              />

              <Form.Control.Feedback type="invalid">
                Insira um número de telefone.
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3" controlId="agent.phone">
              <Form.Label>
                Celular{' '}
                {body.email === '' && body.landline === '' && (
                  <span className="text-danger">*</span>
                )}
              </Form.Label>
              <MaskedInput
                mask={[
                  '(',
                  /[1-9]/,
                  /\d/,
                  ')',
                  ' ',
                  /\d/,
                  /\d/,
                  /\d/,
                  /\d/,
                  /\d/,
                  '-',
                  /\d/,
                  /\d/,
                  /\d/,
                  /\d/
                ]}
                placeholder="(00) 00000-0000"
                required={body.email === '' && body.landline === ''}
                guide={false}
                value={body.phone}
                onChange={e =>
                  setBody(prevBody => ({ ...prevBody, phone: e.target.value }))
                }
                render={(ref, props) => <Form.Control ref={ref} {...props} />}
              />

              <Form.Control.Feedback type="invalid">
                Insira um número de celular.
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                Tags
                <OverlayTrigger
                  overlay={
                    <Tooltip>
                      Tags são usadas para categorizar os contatos e podem ser
                      utilizadas para filtrar contatos em listas.
                    </Tooltip>
                  }
                >
                  <span className="ms-1">
                    <FontAwesomeIcon icon={faQuestionCircle} size="sm" />
                  </span>
                </OverlayTrigger>
              </Form.Label>
              <TagSearch onAddTag={handleAddTag} />
              <div className="mt-1">
                {body.tags.map((tag, index) => (
                  <Badge
                    key={index}
                    bg="light"
                    text="dark"
                    className="me-1 p-2"
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleRemoveTag(tag)}
                  >
                    {tag} &times;
                  </Badge>
                ))}
              </div>
            </Form.Group>

            <Form.Group as={Flex} className="mb-2">
              <Flex
                alignItems="center"
                justifyContent="between"
                className="me-3 w-100"
              >
                <Form.Label>Ativo</Form.Label>
                <Form.Check
                  type="switch"
                  value={body.inactive}
                  checked={!body.inactive}
                  disabled
                  onChange={e => setBody({ ...body, inactive: !body.inactive })}
                />
              </Flex>
            </Form.Group>

            <Form.Group as={Flex} className="mb-1">
              <Flex
                alignItems="center"
                justifyContent="between"
                className="me-3 w-100"
              >
                <Form.Label>Tem acesso ao portal do cliente?</Form.Label>
                <Form.Check
                  type="switch"
                  value={body.isPortalUser}
                  checked={body.isPortalUser}
                  onChange={e =>
                    setBody({ ...body, isPortalUser: !body.isPortalUser })
                  }
                />
              </Flex>
            </Form.Group>

            <Form.Group as={Flex} className="ms-3 mb-3">
              {body.isPortalUser && (
                <Flex
                  alignItems="center"
                  justifyContent="between"
                  className="me-3 w-100"
                >
                  <Form.Label>
                    Administrador
                    <OverlayTrigger
                      overlay={
                        <Tooltip>
                          Usuários administradores podem visualizar todos os
                          tickets dos clientes associados a eles.
                        </Tooltip>
                      }
                    >
                      <span className="ms-1">
                        <FontAwesomeIcon icon={faQuestionCircle} size="sm" />
                      </span>
                    </OverlayTrigger>
                  </Form.Label>
                  <Form.Check
                    type="switch"
                    value={body.isAdmin}
                    checked={body.isAdmin}
                    onChange={e => setBody({ ...body, isAdmin: !body.isAdmin })}
                  />
                </Flex>
              )}
            </Form.Group>

            <Form.Group controlId="contact.customer">
              <Form.Label>
                Cliente principal <span className="text-danger">*</span>
              </Form.Label>
              <SearchInputWithSuggestions
                searchTerm={filters.name}
                required={true}
                placeholder="Digite para pesquisar..."
                suggestions={customers}
                showSuggestions={showMenuMainCustomer}
                onSearchChange={e => {
                  setFilters({ ...filters, name: e.target.value });
                  setShowMenuMainCustomer(e.target.value.length > 0);
                }}
                onSuggestionSelect={handleCustomerSelect}
                renderSuggestionItem={customer => (
                  <div
                    key={customer.id}
                    className="px-3 py-2 border-bottom cursor-pointer hover-bg-200"
                    onClick={() => handleCustomerSelect(customer)}
                    role="button"
                    style={{
                      transition: 'background-color 0.2s'
                    }}
                  >
                    <div className="fw-bold">{customer.name}</div>
                    <div className="text-muted small">
                      {customer.type === 'JURIDICAL_PERSON' ? (
                        <>
                          <FontAwesomeIcon icon={faBuilding} className="me-1" />{' '}
                          Pessoa Jurídica
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faUser} className="me-1" />{' '}
                          Pessoa Física
                        </>
                      )}
                    </div>
                  </div>
                )}
              />

              <Form.Control.Feedback type="invalid">
                Insira um cliente.
              </Form.Control.Feedback>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="falcon-default" onClick={handleClose}>
            Fechar
          </Button>
          <Button variant="primary" onClick={onSubmit}>
            <FontAwesomeIcon icon={faSave} className="me-2" />
            Salvar
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ContactRegistrationModal;
